import { Button, List, Popover } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import CustomWarning from '../../../shared/components/CustomWarning';
import LayerPermissionsModal from './LayerPermissionsModal';
import React, { useMemo, useState } from 'react';
import { DrawerItem } from './LegendDrawer';
import { useLayerProperitesContext } from '../../../context/LayerPropertiesContext';
import { seedLayerProperties } from '../LayerProperties/Symbology/symbologyConfig.utils';
import MapPortalService from '../../../services/MapPortal/mapportal.service';
import { useLegendForm, useLegendUI } from '../../../context/MapLegendContext';
import { vectorLayerType } from '../../../shared/utils/vectorLayerType';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import { LayerAction } from '../../../enums/mapPortalActions';
import { layerActionAccess } from '../constants';
import './LayerOption.scss';

interface LayerOptionsButtonProps {
  item: DrawerItem;
  onZoomToLayer: (option: string) => void;
  setModalVisible: (value: boolean) => void;
  setAttributeModalVisible: (value: boolean) => void;
  onDuplicateLayer: (layerName: string) => void;
  onDownloadLayer: (item: DrawerItem) => void;
  onSaveLayer: (value: boolean) => void;
  handleTableClick: (value: boolean, layerName: string, layerTitle: string) => void;
  mapId: string;
  setIsLayerLoading: (loading: boolean) => void;
  onLayerUpdateComplete?: () => Promise<void>;
  onCreateGroup?: () => void;
}

const LayerOptions: React.FC<LayerOptionsButtonProps> = ({
  item,
  mapId,
  onZoomToLayer,
  setModalVisible,
  setAttributeModalVisible,
  onDuplicateLayer,
  onDownloadLayer,
  onSaveLayer,
  handleTableClick,
  setIsLayerLoading,
  onLayerUpdateComplete,
  onCreateGroup,
}) => {
  const [visible, setVisible] = useState(false);
  const [removeWarningOpen, setRemoveWarningOpen] = useState(false);
  const [layerPermissionsOpen, setLayerPermissionsOpen] = useState(false);
  const { setState } = useLayerProperitesContext();
  const { deleteLayer } = MapPortalService();
  const { mutateAsync: deleteLayerAsync } = useMutation(deleteLayer());
  const { setFolderS3Key } = useLegendForm();
  const { setSelectedTab, setReuploadRequested } = useLegendUI();
  const { canAccess } = useAuth();

  // Role denial drops the row; `hidden` covers the orthogonal layer-type rules.
  const options = useMemo(
    () =>
      [
        {
          label: LayerAction.ZOOM_TO_LAYER,
          disabled: false,
          hidden: false,
        },
        {
          label: LayerAction.SHOW_PROPERTIES,
          disabled: false,
          hidden: false,
        },
        {
          label: LayerAction.SHOW_TABLE,
          disabled: false,
          hidden: vectorLayerType(item?.layerType),
        },
        {
          label: LayerAction.DOWNLOAD_LAYER,
          disabled: !item.downloadable,
          hidden: vectorLayerType(item?.layerType),
        },
        {
          label: LayerAction.DUPLICATE_LAYER,
          disabled: false,
          hidden: vectorLayerType(item?.layerType),
        },
        {
          label: LayerAction.SAVE_LAYER_AS,
          disabled: false,
          // Hidden for raster/WMS: the underlying ogr2ogr export is vector-only.
          hidden: vectorLayerType(item?.layerType),
        },
        {
          label: LayerAction.REMOVE_LAYER,
          disabled: false,
          // Available for ALL layer types — the backend cleanup is type-aware: it drops
          // the coveragestore for rasters and the featuretype + PostGIS table for vectors.
          hidden: false,
        },
        {
          label: LayerAction.CREATE_GROUP,
          disabled: false,
          hidden: vectorLayerType(item?.layerType),
        },
        {
          label: LayerAction.LAYER_PERMISSIONS,
          disabled: false,
          hidden: vectorLayerType(item?.layerType),
        },
        {
          label: LayerAction.RE_UPLOAD_DATA,
          disabled: false,
          // Hidden for raster/WMS — re-upload is a shapefile/KML/etc. flow.
          hidden: vectorLayerType(item?.layerType),
        },
      ].filter((option) => canAccess(layerActionAccess(option.label))),
    [item?.layerType, item.downloadable, canAccess],
  );

  const handleRemoveConfirm = async () => {
    setRemoveWarningOpen(false);
    setIsLayerLoading(true);
    try {
      await deleteLayerAsync({ mapId: String(mapId), layerId: String(item.id) });
      if (onLayerUpdateComplete) {
        await onLayerUpdateComplete();
      } else {
        setIsLayerLoading(false);
      }
    } catch {
      setIsLayerLoading(false);
    }
  };

  const handleOptionClick = (option: LayerAction) => {
    // The trigger seeds LayerPropertiesContext before any row is picked.
    if (!canAccess(layerActionAccess(option))) return;

    if (option === LayerAction.SHOW_PROPERTIES) {
      setModalVisible(true);
    } else if (option === LayerAction.SHOW_TABLE) {
      handleTableClick(true, item.uniqueLayerName, item.layerTitle ?? '');
      setAttributeModalVisible(true);
    } else if (option === LayerAction.ZOOM_TO_LAYER) {
      onZoomToLayer(item?.uniqueLayerName);
    } else if (option === LayerAction.DUPLICATE_LAYER) {
      onDuplicateLayer(item?.uniqueLayerName);
    } else if (option === LayerAction.DOWNLOAD_LAYER) {
      onDownloadLayer(item);
    } else if (option === LayerAction.SAVE_LAYER_AS) {
      onSaveLayer(true);
    } else if (option === LayerAction.REMOVE_LAYER) {
      setRemoveWarningOpen(true);
    } else if (option === LayerAction.CREATE_GROUP) {
      onCreateGroup?.();
    } else if (option === LayerAction.LAYER_PERMISSIONS) {
      setLayerPermissionsOpen(true);
    } else if (option === LayerAction.RE_UPLOAD_DATA) {
      // US-101368 Scenario 1: open Layer Properties → Settings and signal
      // the tab to immediately fire its re-upload flow on mount. The
      // popover-trigger onClick already pushed this layer's data into
      // LayerPropertiesContext, so the tab will operate on the right layer.
      setSelectedTab(0);
      setReuploadRequested(true);
      setModalVisible(true);
    }
    setVisible(false);
  };

  const content = (
    <List
      size="small"
      dataSource={options}
      className="layer-options__list"
      renderItem={(item) => (
        <List.Item
          onClick={() => !item.disabled && handleOptionClick(item.label)}
          style={{
            cursor: item.disabled ? 'not-allowed' : 'pointer',
            padding: '5px 16px',
            backgroundColor: item.disabled ? '#f5f5f5' : 'white',
            color: item.disabled ? '#b0b0b0' : 'inherit',
            display: item.hidden ? 'none' : 'block',
          }}
        >
          {item.label}
        </List.Item>
      )}
    />
  );

  return (
    <>
      <CustomWarning
        open={removeWarningOpen}
        onCancel={() => setRemoveWarningOpen(false)}
        onConfirm={handleRemoveConfirm}
        title="Remove Layer from Map?"
        message="This will not remove the layer from the file manager."
        cancelText="Cancel"
        confirmText="Remove"
      />

      <LayerPermissionsModal
        open={layerPermissionsOpen}
        onClose={() => setLayerPermissionsOpen(false)}
        resourceId={String(item.id)}
        readOnly={!canAccess(layerActionAccess(LayerAction.MANAGE_LAYER_PERMISSIONS))}
      />

      <Popover
        content={content}
        trigger="click"
        open={visible}
        onOpenChange={setVisible}
        placement="bottomRight"
        overlayClassName="layer-options__popover"
      >
        <Button
          type="text"
          className="layer-options__trigger"
          aria-label="Layer options"
          icon={<MoreOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            setState(() => seedLayerProperties(item));
            setFolderS3Key?.(item.s3Key);
            setVisible(!visible);
          }}
        />
      </Popover>
    </>
  );
};

// Perf: a legend with many layers re-renders the LayerOptions popover trigger
// for every row on every parent re-render. React.memo + stable callbacks
// upstream (the handleX functions are useCallback-d in LegendDrawer) lets us
// skip the per-row re-render unless the row's own data actually changed.
export default React.memo(LayerOptions);
